import type { FieldValues, Path } from 'react-hook-form';
import {
  Controller,
  type ControllerRenderProps,
  useFormContext,
  useFormState,
} from 'react-hook-form';
import { LseDropdown, type LseDropdownProps } from './LseDropdown';

type PresetProps =
  | 'value'
  | 'onChange'
  | 'onBlur'
  | 'name'
  | 'error'
  | 'helperText';

export type RhfLseDropdownProps<T extends FieldValues> = Omit<
  LseDropdownProps,
  PresetProps
> & {
  name: Path<T>;
  helperText?: LseDropdownProps['helperText'];
  onValueChange?: (lseId: number | null) => void;
};

export function RhfLseDropdown<T extends FieldValues>({
  name,
  helperText,
  onValueChange,
  ...rest
}: RhfLseDropdownProps<T>) {
  const { control, trigger } = useFormContext<T>();
  const { errors } = useFormState({ control, name });
  const fieldError = errors[name];
  const errorMessage =
    typeof fieldError?.message === 'string' ? fieldError.message : undefined;
  const invalid = Boolean(fieldError);

  const renderDropdown = ({
    field,
  }: {
    field: ControllerRenderProps<T, Path<T>>;
  }) => {
    const value = typeof field.value === 'number' ? field.value : null;

    const handleChange = (lseId: number | null) => {
      field.onChange(lseId);
      onValueChange?.(lseId);
      void trigger(name);
    };

    const handleBlur = () => {
      field.onBlur();
      void trigger(name);
    };

    return (
      <LseDropdown
        {...rest}
        name={field.name}
        value={value}
        onChange={handleChange}
        onBlur={handleBlur}
        error={invalid}
        helperText={invalid ? errorMessage : helperText}
      />
    );
  };

  return (
    <Controller<T> name={name} control={control} render={renderDropdown} />
  );
}
